"use client";

import { useEffect, useRef } from "react";
import { openLoadingOnboardingModal } from "@/src/components/ChatOnboarding/Steps/LoadingStep/openLoadingOnboardingModal";
import { openPortfolioInsightOnboardingModal } from "@/src/components/ChatOnboarding/Steps/DiagnosticStep/openPortfolioInsightOnboardingModal";
import type { PortfolioInsightOnboardingModalResult } from "@/src/components/ChatOnboarding/Steps/DiagnosticStep/types";
import { openTrackersSetupOnboardingModal } from "@/src/components/ChatOnboarding/Steps/ResultStep/openTrackersSetupOnboardingModal";
import type { ChatOnboardingStepId } from "@/src/components/ChatOnboarding/types";
import type { LoadingModalAction } from "@/src/components/ChatOnboarding/Steps/LoadingStep/types";
import type {
  TrackersSetupModalAction,
  TrackersSetupModalPayload,
} from "@/src/components/ChatOnboarding/Steps/ResultStep/types";

export type OnboardingStepModalHostProps = {
  /** Parent-driven step; `null` / `undefined` = no modal. */
  activeStep?: ChatOnboardingStepId | null;
  resultPayload?: TrackersSetupModalPayload;
  onLoadingComplete?: (action: LoadingModalAction) => void;
  onDiagnosticComplete?: (
    result: PortfolioInsightOnboardingModalResult,
  ) => void;
  onResultComplete?: (action: TrackersSetupModalAction) => void;
};

/**
 * Opens Loading / Diagnostic (⑥→⑦) / Result modals when parent sets {@link activeStep}.
 */
export const useOnboardingStepModalsFromProps = ({
  activeStep,
  resultPayload,
  onLoadingComplete,
  onDiagnosticComplete,
  onResultComplete,
}: OnboardingStepModalHostProps): void => {
  const openedStepRef = useRef<ChatOnboardingStepId | null>(null);

  useEffect(() => {
    if (!activeStep) {
      openedStepRef.current = null;
      return;
    }

    if (openedStepRef.current === activeStep) {
      return;
    }
    openedStepRef.current = activeStep;

    if (activeStep === "loading") {
      void openLoadingOnboardingModal().then((result) => {
        openedStepRef.current = null;
        onLoadingComplete?.(result.action);
      });
      return;
    }

    if (activeStep === "diagnostic") {
      void openPortfolioInsightOnboardingModal().then((result) => {
        openedStepRef.current = null;
        onDiagnosticComplete?.(result);
      });
      return;
    }

    if (activeStep === "result") {
      void openTrackersSetupOnboardingModal({ payload: resultPayload }).then(
        (result) => {
          openedStepRef.current = null;
          onResultComplete?.(result.action);
        },
      );
    }
  }, [
    activeStep,
    resultPayload,
    onLoadingComplete,
    onDiagnosticComplete,
    onResultComplete,
  ]);
};
